import React, { useCallback } from "react";
import SalesDocDetails from "../../_components/Sales/SalesDocDetails";
import { FiCalendar } from "react-icons/fi";
import { FieldInput, InfoRow } from "../../_components/Sales/SalesShared";
import {
    getQuotationById,
    updateQuotation,
    createQuotation,
} from "../../_services/sales.service";
import { getCustomers } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";

/* Customer data fetchers */
let customerCache = {};
async function getCachedCustomer(customerName) {
    if (!customerName) return []; // Return an empty array if no customer name is provided

    if (customerCache[customerName]) return customerCache[customerName];

    const res = await getCustomers({
        pageSize: 1000,
        page: 1,
        search: customerName,
        searchType: "name",
    });

    customerCache[customerName] = res?.data || []; // Cache the result to prevent redundant API calls
    return customerCache[customerName];
}

/**
 * Returns only ACTIVE customers for the name dropdown.
 * Inactive customers will not appear in the selection list.
 */
async function fetchCustomerNames() {
    const res = await getCustomers({ pageSize: 1000, page: 1 });
    const names = (res?.data || [])
        /* FIX: only include customers whose status is Active */
        .filter(r => {
            const s = (r.status || r.Status || "").toLowerCase();
            return s === "active";
        })
        .map((r) => r.name)
        .filter(Boolean);
    return [...new Set(names)].sort();
}

/**
 * Remove empty entries appearing in the Contact Person dropdown.
 * Added a condition in fetchContactPersons to ensure no empty contact names show up.
 */
async function fetchContactPersons(customerName) {
    if (!customerName) return [];
    const data = await getCachedCustomer(customerName);
    return data
        .filter((r) => {
            const s = (r.status || r.Status || "").toLowerCase();
            return r.name === customerName && !!r.attention && s === "active";
        })
        .map((r) => r.attention ?? "") // Ensure no undefined contact persons
        .filter(Boolean) // Filter out blank or null entries
        .filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates
}

/**
 * Given a customer name, returns their credit terms and address.
 */
async function fetchCustomerDetails(customerName) {
    if (!customerName) return {};
    const data = await getCachedCustomer(customerName);
    const match = data.find((r) => r.name === customerName);
    if (!match) return {};
    return {
        paymentTerms: match.creditTerms || "",
        customerAddress: match.billingAddress || match.address || match.shippingAddress || "",
        shippingAddress: match.shippingAddress || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}

/* Quotation-specific extra field: Valid Until
   - dateMin is now passed from SalesDocDetails (today's date when adding, undefined when editing)
   - So past dates are also blocked for Valid Until when creating a new quotation.
*/
const quotationExtra = {
    left: ({ draft, onChange, errors, edit, dateMin }) =>
        edit ? (
            <FieldInput
                label="Valid Until"
                name="validUntil"
                type="date"
                value={draft?.validUntil || ""}
                onChange={onChange}
                /* FIX: min is the later of today (dateMin) and the selected date */
                min={draft?.date || dateMin}
                required
                error={errors?.validUntil}
            />
        ) : (
            <InfoRow icon={FiCalendar} label="Valid Until">
                {draft?.validUntil || "—"}
            </InfoRow>
        ),
    right: null,
};

/* ── Component ──────────────────────────────────────────────────────────── */
export default function QuotationDetails() {
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);

    return (
        <SalesDocDetails
            docType="Quotation"
            basePath="/sales/quotations"
            fetchById={getQuotationById}
            updateItem={updateQuotation}
            createItem={createQuotation}
            fetchCustomerNames={fetchCustomerNames}
            fetchContactPersons={fetchContactPersons}
            fetchCustomerDetails={fetchCustomerDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            statuses={["Draft", "Pending for Approval", "Approved", "Rejected"]}
            emptyDraft={{ validUntil: "" }}
            extraFields={quotationExtra}
            hideCurrency={true}
            hidePaymentTerms={false}
        />
    );
}